import { useMutation, useQuery } from '@apollo/client';

import { ConversationStarter, ConversationStarters } from '@eluve/blocks';
import {
  Heading,
  LoadingIndicator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import {
  getGlobalConversationStarterDefaultsQuery,
  insertConversationStarterDefaultMutation,
  updateConversationStarterDefaultMutation,
} from './conversationStarterDefaults.operations';

export const GlobalConversationStarterDefaultsPage: React.FC = () => {
  const toast = useToast();

  const { data, loading, refetch } = useQuery(
    getGlobalConversationStarterDefaultsQuery,
  );

  const globalDefaults = data?.conversationStarterDefaults ?? [];
  const globalRow = globalDefaults[0];
  const starters = (globalRow?.starters ?? []) as ConversationStarter[];

  const [insertDefault] = useMutation(insertConversationStarterDefaultMutation);
  const [updateDefault] = useMutation(updateConversationStarterDefaultMutation);

  const handleSave = async (updatedStarters: ConversationStarter[]) => {
    if (globalRow) {
      await updateDefault({
        variables: {
          id: globalRow.id,
          starters: updatedStarters,
          specialty: null,
          language: null,
        },
      });
    } else {
      await insertDefault({
        variables: {
          tenantId: null,
          userId: null,
          specialty: null,
          language: null,
          starters: updatedStarters,
        },
      });
    }

    await refetch();
    toast.success('Global conversation starters updated');
  };

  if (loading && !data) {
    return (
      <Stack
        className="py-16"
        direction="horizontal"
        align="center"
        justify="center"
      >
        <LoadingIndicator />
      </Stack>
    );
  }

  return (
    <Stack gap={6} className="p-6">
      <Stack gap={1}>
        <Heading size="xl" weight="semibold">
          Global Conversation Starter Defaults
        </Heading>
        <Text size="xs" color="secondary">
          These starters are shown to all users who don't have tenant-specific
          or personal overrides configured.
        </Text>
      </Stack>

      <ConversationStarters initialStarters={starters} onSave={handleSave} />
    </Stack>
  );
};
